import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import Logger from '@/utils/logger';
import { 
  getCurrentSession, 
  checkUserProfile, 
  signIn as authSignIn, 
  signOut as authSignOut, 
  subscribeToAuthChanges, 
  subscribeToUserChanges 
} from '@/services/authService';

interface AuthContextProps {
  user: User | null;
  isLoading: boolean;
  hasUserRecord: boolean;
  hasProfile: boolean;
  isOnboardingRequired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserProfileStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  isLoading: false,
  hasUserRecord: true,
  hasProfile: false,
  isOnboardingRequired: false,
  signIn: async () => {},
  signOut: async () => {},
  refreshUserProfileStatus: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // We'll default to `true` and stop forcing a sign-out if the row isn't there yet
  const [hasUserRecord, setHasUserRecord] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    const checkUser = async () => {
      setIsLoading(true);
      try {
        const session = await getCurrentSession();

        if (!session) {
          setUser(null);
          // Let finally block handle isLoading
          return;
        }

        setUser(session.user);

        // Determine profile status without causing navigation here
        const profileExists = await checkUserProfile(session.user.id);
        setHasProfile(profileExists);
        setHasUserRecord(true); // Assuming if session.user exists, a user record exists
      } catch (error) {
        Logger.error('Error during checkUser execution', error as Error, {
          component: 'AuthProvider'
        });
        /* keep the current session; we'll retry later */
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();

    const { data: { subscription: authSubscription } } = subscribeToAuthChanges((newUser) => {
      (async () => {
        const isLogin = !userRef.current && !!newUser;
        const isLogout = !!userRef.current && !newUser;

        if (isLogout) {
          setUser(null);
          setHasProfile(false);
          return;
        }

        if (!newUser) {
          return;
        }

        // Only show loading spinner on login, not on token refresh
        if (isLogin) {
          setIsLoading(true);
        }

        setUser(newUser);
        setHasUserRecord(true);

        try {
          const profileExists = await checkUserProfile(newUser.id);
          setHasProfile(profileExists);
        } catch (err) {
          Logger.warn('Profile lookup failed after auth change', {
            component: 'AuthProvider',
            userId: newUser.id,
            err,
          });
          setHasProfile(false);
        } finally {
          if (isLogin) {
            setIsLoading(false);
          }
        }
      })();
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  // ────────────────────────────────────────────────────────────────
  // React to changes in the *current* user's DB row (e.g., it gets
  // deleted by an admin).  Needs to re-subscribe whenever user.id
  // changes, so we keep it in its own effect.
  useEffect(() => {
    if (!user) return;

    let subscription: { unsubscribe: () => void } | null = null;
    
    try {
      subscription = subscribeToUserChanges(user.id, async () => {
        try {
          await authSignOut();
        } catch (error) {
          // Sign-out after user deletion may throw; safe to ignore
        }
        setUser(null);
      });
    } catch (error) {
      Logger.warn('Failed to subscribe to user changes, continuing without real-time updates', {
        component: 'AuthProvider',
        userId: user.id,
        error
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [user]);

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await authSignIn(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      setUser(null);
      localStorage.clear();
      await authSignOut();
    } finally {
      window.location.replace('/');
    }
  };

  const refreshUserProfileStatus = async () => {
    if (user) {
      try {
        const profileExists = await checkUserProfile(user.id);
        setHasProfile(profileExists);
      } catch (error) {
        Logger.error('Error refreshing user profile status', error as Error, { component: 'AuthProvider' });
      }
    }
  };

  // Don't redirect to the onboarding wizard while the initial
  // auth/profile lookup is still running – this is what caused
  // the momentary flash on page refresh.
  const isOnboardingRequired =
    !!user && hasUserRecord && !hasProfile && !isLoading;

  const value = {
    user,
    isLoading,
    hasUserRecord,
    hasProfile,
    isOnboardingRequired,
    signIn,
    signOut,
    refreshUserProfileStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
